import { Icon } from '@iconify/react';
import menu2Fill from '@iconify/icons-eva/menu-2-fill';
// material

import { alpha, styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
// hooks
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import useLocales from '../../hooks/useLocales';
// components
import { MHidden } from '../../components/@material-extend';
import LanguagePopover from './LanguagePopover';
// import ContactsPopover from './ContactsPopover';
import NotificationsPopover from './NotificationsPopover';
import AccountPopover from './AccountPopover';
import Logout from './Logout';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 64;

const RootStyle = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  backgroundColor: alpha('#FDFFFD', 0.72),
  [theme.breakpoints.up('lg')]: {
    width: `calc(100% - ${DRAWER_WIDTH + 1}px)`
  }
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: 0,
    borderBottom: '1px solid #e5e7eb'
  }
}));

// ----------------------------------------------------------------------

type DashboardNavbarProps = {
  onOpenSidebar: VoidFunction;
};

export default function DashboardNavbar({ onOpenSidebar }: DashboardNavbarProps) {
  const { isCollapse } = useCollapseDrawer();
  const location = useLocation();
  const { trans } = useLocales();

  const breadcrumbMap: Record<string, { label: string; parent?: string }> = {
    '/dashboard/platform-resource-library': {
      label: trans('breadcrumb.platformResourceLib'),
      parent: trans('breadcrumb.home')
    },
    '/dashboard/curriculum-activities': {
      label: trans('breadcrumb.curriculumActivities'),
      parent: trans('breadcrumb.home')
    },
    '/dashboard/school-resource': {
      label: trans('breadcrumb.schoolResourceLib'),
      parent: trans('breadcrumb.home')
    },
    '/dashboard/app': { label: trans('breadcrumb.home') },
    '/dashboard/school-based': {
      label: trans('breadcrumb.schoolBased'),
      parent: trans('breadcrumb.home')
    },
    '/dashboard/SchoolCourse': {
      label: trans('breadcrumb.schoolCourse'),
      parent: trans('breadcrumb.home')
    },
    '/dashboard/parentChildTask/list': {
      label: trans('breadcrumb.parentChildTask'),
      parent: trans('breadcrumb.home')
    }
  };

  // 根据当前路由生成面包屑
  const getBreadcrumbs = () => {
    const path = location.pathname;
    const breadcrumbInfo = breadcrumbMap[path];

    if (!breadcrumbInfo) {
      // 尝试匹配动态路由
      const dynamicRoutes = [
        {
          pattern: '/dashboard/curriculum-activities/edit',
          label: trans('breadcrumb.curriculumActivitiesEdit'),
          parent: trans('breadcrumb.curriculumActivities')
        },
        {
          pattern: '/dashboard/curriculum-activities/new',
          label: trans('breadcrumb.curriculumActivitiesNew'),
          parent: trans('breadcrumb.curriculumActivities')
        },
        {
          pattern: '/dashboard/curriculum-activities/view-state',
          label: trans('breadcrumb.curriculumActivitiesView'),
          parent: trans('breadcrumb.curriculumActivities')
        },
        {
          pattern: '/dashboard/school-based/edit',
          label: trans('breadcrumb.schoolBasedEdit'),
          parent: trans('breadcrumb.schoolBased')
        },
        {
          pattern: '/dashboard/school-based/add',
          label: trans('breadcrumb.schoolBasedNew'),
          parent: trans('breadcrumb.schoolBased')
        },
        {
          pattern: '/dashboard/school-based/view-state',
          label: trans('breadcrumb.schoolBasedView'),
          parent: trans('breadcrumb.schoolBased')
        },
        {
          pattern: '/dashboard/SchoolCourse',
          label: trans('breadcrumb.schoolCourse'),
          parent: trans('breadcrumb.home')
        },
        {
          pattern: '/dashboard/parentChildTask/add',
          label: trans('breadcrumb.parentChildTaskNew'),
          parent: trans('breadcrumb.parentChildTask')
        },
        {
          pattern: '/dashboard/parentChildTask/edit',
          label: trans('breadcrumb.parentChildTaskEdit'),
          parent: trans('breadcrumb.parentChildTask')
        },
        {
          pattern: '/dashboard/parentChildTask/view',
          label: trans('breadcrumb.parentChildTaskView'),
          parent: trans('breadcrumb.parentChildTask')
        }
      ];

      for (const route of dynamicRoutes) {
        if (path.startsWith(route.pattern)) {
          return { label: route.label, parent: route.parent };
        }
      }

      return null;
    }

    return breadcrumbInfo;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <RootStyle
      sx={{
        ...(isCollapse && {
          width: { lg: `calc(100% - ${COLLAPSE_WIDTH}px)` }
        })
      }}
    >
      <ToolbarStyle>
        {/* 面包屑导航 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
          {breadcrumbs ? (
            <>
              {breadcrumbs.parent && (
                <>
                  <Typography
                    sx={{
                      color: '#666',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    {breadcrumbs.parent}
                  </Typography>
                  <Typography sx={{ color: '#ccc', fontSize: 14 }}>›</Typography>
                </>
              )}
              <Typography
                sx={{
                  color: '#666',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                {breadcrumbs.label}
              </Typography>
            </>
          ) : (
            <Typography
              sx={{
                color: '#666',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: 14
              }}
            >
              {trans('breadcrumb.home')}
            </Typography>
          )}
        </Box>

        <MHidden width="lgUp">
          <IconButton onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Icon icon={menu2Fill} />
          </IconButton>
        </MHidden>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1.5 }}>
          <LanguagePopover />
          {/* <NotificationsPopover /> */}
          <AccountPopover />
          {/* <Logout /> */}
          {/* <ContactsPopover /> */}
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
